// SPDX-License-Identifier: Elastic-2.0
/**
 * Toast — single toast row inside the ToastProvider stack.
 *
 * Visual primitive only — it does NOT manage its own lifetime. The
 * provider owns timers, ordering, and the visible/queued split; this
 * component just renders one notification with the correct ARIA role
 * and emits dismiss/action callbacks.
 *
 * Accessibility:
 *  - info / success → role="status" (polite — won't interrupt screen reader)
 *  - warning / error → role="alert"  (assertive — more urgent)
 *  - The parent stack container carries `aria-live="polite"` so toasts
 *    appended dynamically are announced.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import styles from "./Toast.module.css";

export type ToastKind = "info" | "success" | "warning" | "error";

/** Options accepted by `useToast().show(opts)`. */
export interface ToastOptions {
  /** Visual kind; also drives the ARIA role. */
  kind: ToastKind;
  /** Optional bold title shown above the message. */
  title?: string;
  /** Required descriptive text. */
  message: string;
  /**
   * Auto-dismiss timeout in milliseconds. Defaults to 4000 in the
   * provider. Pass `0` to disable auto-dismiss (rare; consider a Banner
   * for persistent messages instead).
   */
  durationMs?: number;
  /** Optional inline action button (e.g. "Undo"). */
  action?: { label: string; onClick: () => void };
}

export interface ToastInstance extends ToastOptions {
  /** Stable id assigned by the provider for dismiss lookup + React keys. */
  id: string;
}

export interface ToastProps {
  toast: ToastInstance;
  /** True while the exit animation is playing (provider-driven). */
  exiting?: boolean;
  /** Called by the close button. */
  onDismiss: (id: string) => void;
}

const KIND_CLASS: Record<ToastKind, string> = {
  info: styles.info ?? "",
  success: styles.success ?? "",
  warning: styles.warning ?? "",
  error: styles.error ?? "",
};

function ariaRole(kind: ToastKind): "status" | "alert" {
  return kind === "warning" || kind === "error" ? "alert" : "status";
}

export function Toast({ toast, exiting, onDismiss }: ToastProps): ReactElement {
  const { t } = useTranslation();
  const { id, kind, title, message, action } = toast;
  const classes = [styles.toast, KIND_CLASS[kind], exiting ? styles.exiting : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      role={ariaRole(kind)}
      data-toast-kind={kind}
      data-toast-id={id}
    >
      <div className={styles.body}>
        {title ? <div className={styles.title}>{title}</div> : null}
        <div className={styles.message}>{message}</div>
        {action ? (
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.action}
              onClick={() => {
                action.onClick();
                onDismiss(id);
              }}
            >
              {action.label}
            </button>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className={styles.close}
        aria-label={t("feedback.dismiss")}
        onClick={() => onDismiss(id)}
      >
        {"×"}
      </button>
    </div>
  );
}
